import { useState, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useInventory } from "@/contexts/InventoryContext";
import { useAuth } from "@/contexts/AuthContext";
import { VoucherType, VoucherLine } from "@/lib/accounting/types";
import { MovementType } from "@/lib/inventory/types";
import { Button } from "@/components/ui/button";
import { FormModal } from "@/components/form/FormModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Package, ArrowRightLeft, Truck, ClipboardCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface InventoryVoucherFormProps {
  type: "Stock Journal" | "Delivery Note" | "Receipt Note" | "Physical Stock";
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function InventoryVoucherForm({
  type,
  trigger,
  onSuccess,
  isOpen,
  onOpenChange,
}: InventoryVoucherFormProps) {
  const { stockItems, ledgers, createVoucher } = useAccounting();
  const { recordMovement } = useInventory();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [quantity, setQuantity] = useState<string>("1");
  const [rate, setRate] = useState<string>("");
  const [reference, setReference] = useState("");
  const [godownOrBranch, setGodownOrBranch] = useState("Main Godown");
  const [narration, setNarration] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const physicalGoods = useMemo(() => stockItems.filter((i) => i.itemType !== "service"), [stockItems]);
  const selectedItem = useMemo(() => stockItems.find((i) => i.id === selectedItemId), [stockItems, selectedItemId]);

  const numQty = parseFloat(quantity) || 0;
  const numRate = parseFloat(rate) || (selectedItem ? selectedItem.standardRate || selectedItem.costRate || 0 : 0);
  const totalVal = Math.round(numQty * numRate * 100) / 100;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedItem) {
      toast({ title: "Item Required", description: "Please select an inventory item.", variant: "destructive" });
      return;
    }
    if (numQty <= 0) {
      toast({ title: "Invalid Quantity", description: "Quantity must be greater than zero.", variant: "destructive" });
      return;
    }

    if (type === "Delivery Note" && numQty > Number(selectedItem.currentQty || 0)) {
      toast({
        title: "Stock Exceeded",
        description: `Cannot dispatch ${numQty} units of "${selectedItem.name}". Only ${Number(selectedItem.currentQty || 0)} ${selectedItem.unit || "units"} available in stock.`,
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      // Find stock-in-hand ledger or general inventory ledger
      const stockLedger =
        ledgers.find((l) => l.name.toLowerCase().includes("stock") || l.group === "Current Assets") ||
        ledgers[0];

      // Double-entry representation for inventory adjustment
      const lines: VoucherLine[] = [
        {
          id: crypto.randomUUID(),
          ledgerId: stockLedger.id,
          ledgerName: stockLedger.name,
          debit: type === "Receipt Note" || type === "Physical Stock" ? totalVal : 0,
          credit: type === "Delivery Note" ? totalVal : 0,
          narration: `${type} for ${selectedItem.name} (${numQty} ${selectedItem.unit})`,
        },
        {
          id: crypto.randomUUID(),
          ledgerId: stockLedger.id,
          ledgerName: stockLedger.name,
          debit: type === "Delivery Note" ? totalVal : 0,
          credit: type === "Receipt Note" || type === "Physical Stock" ? totalVal : 0,
          narration: `Offset for ${type}`,
        },
      ];

      const defaultNarration = `${type}: ${selectedItem.name} | Qty: ${numQty} ${selectedItem.unit} @ ₹${numRate} | Location: ${godownOrBranch}${
        reference ? ` | Ref: ${reference}` : ""
      }`;

      const finalNarration = narration.trim() || defaultNarration;

      const result = await createVoucher(
        type as VoucherType,
        lines,
        finalNarration,
        undefined,
        reference.trim() || undefined,
        0, 0, 0, 0,
        [
          {
            id: selectedItem.id,
            name: selectedItem.name,
            qty: numQty,
            rate: numRate,
            gstRate: selectedItem.gstRate || 0,
            amount: totalVal,
            unit: selectedItem.unit,
          },
        ],
        { date }
      );

      if (result.success && result.voucher) {
        // Record inventory movement
        const movType: MovementType =
          type === "Receipt Note"
            ? "In"
            : type === "Delivery Note"
            ? "Out"
            : type === "Physical Stock"
            ? "Adjustment-In"
            : "Transfer-In";

        await recordMovement(
          selectedItem.id,
          movType,
          numQty,
          numRate,
          undefined,
          result.voucher.voucherNumber,
          finalNarration
        );

        toast({
          title: `${type} Created ✓`,
          description: `${result.voucher.voucherNumber}: ${numQty} ${selectedItem.unit} recorded.`,
        });
        setOpen(false);
        setQuantity("1");
        setRate("");
        setReference("");
        setNarration("");
        onSuccess?.();
      } else {
        toast({ title: "Failed", description: result.error || "Unable to save voucher.", variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case "Stock Journal":
        return "Stock Journal (Transfer / Consumption)";
      case "Delivery Note":
        return "Delivery Note (Outward Challan)";
      case "Receipt Note":
        return "Receipt Note (Inward GRN)";
      case "Physical Stock":
        return "Physical Stock Verification";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "Stock Journal":
        return ArrowRightLeft;
      case "Delivery Note":
        return Truck;
      case "Receipt Note":
        return Package;
      case "Physical Stock":
        return ClipboardCheck;
    }
  };

  const IconComp = getIcon();

  return (
    <FormModal
      open={open}
      onOpenChange={setOpen}
      size="transaction"
      trigger={
        trigger !== undefined ? (
          trigger
        ) : isOpen !== undefined ? (
          null
        ) : (
          <Button variant="outline" className="gap-2">
            <IconComp className="h-4 w-4" />
            <span>New {type}</span>
          </Button>
        )
      }
      icon={<IconComp className="h-5 w-5 text-primary" />}
      title={getTitle()}
      description="Manage inventory movements, warehouse transfers, dispatch notes, and physical audits."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          disabled={submitting}
          className="text-xs h-9"
        >
          Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
        </Button>
      }
      footerRight={
        <Button
          type="submit"
          form="inventory-voucher-form"
          disabled={submitting}
          className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9 font-semibold gap-1.5 shadow-sm"
        >
          {submitting ? "Saving..." : `Record ${type}`}
        </Button>
      }
    >
      <form id="inventory-voucher-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Item & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Stock Item *
              </label>
              <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select item..." />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {physicalGoods.map((item) => (
                    <SelectItem key={item.id} value={item.id} className="text-xs">
                      {item.name} · Current: {item.currentQty} {item.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Transaction Date *
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 text-xs font-mono"
                required
              />
            </div>
          </div>

          {/* Quantity & Rate */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Quantity ({selectedItem?.unit || "Units"}) *
              </label>
              <Input
                type="number"
                min="0.01"
                max={type === "Delivery Note" && selectedItem ? Number(selectedItem.currentQty || 0) : undefined}
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-9 text-xs font-mono font-semibold text-right"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Valuation Rate (₹)
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder={selectedItem ? String(selectedItem.standardRate || selectedItem.costRate || 0) : "0.00"}
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="h-9 text-xs font-mono text-right"
              />
            </div>
          </div>

          {/* Godown / Branch & Ref */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Godown / Location
              </label>
              <Input
                type="text"
                value={godownOrBranch}
                onChange={(e) => setGodownOrBranch(e.target.value)}
                placeholder="e.g. Main Godown / Warehouse B"
                className="h-9 text-xs"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Challan / Order Ref No
              </label>
              <Input
                type="text"
                placeholder="e.g. CHN-9021 or PO-401"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="h-9 text-xs font-mono"
              />
            </div>
          </div>

          {/* Narration */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
              Narration
            </label>
            <Input
              type="text"
              placeholder="e.g. Stock verified during month-end audit"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Total Value */}
          {selectedItem && totalVal > 0 && (
            <div className="p-2.5 rounded bg-muted/40 border border-border flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Computed Total Inventory Value:</span>
              <span className="font-mono font-bold text-foreground">
                ₹{totalVal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}

      </form>
    </FormModal>
  );
}
